/**
 * letterLeaflets
 * --------------
 * The leaflets that go out with a triage outcome letter (TDI-387, deliverable
 * 3 of the TDI-379 EMIS admin handover spec). Only pathways whose settings say
 * leaflets ride with the letter (pathwaySettings.js, TDI-385) send any; the
 * autism service writes its signposting into the letter itself.
 *
 * Accepted ADHD referrals carry the two leaflets Jenny Glover (ADHD Assistant
 * Practitioner) gave the service (TDI-385, 29 October 2026). They are static
 * files the site hosts under public/rms/leaflets/, so here a leaflet's url is
 * its site-relative path; the send stores the sending environment's full
 * address. A not accepted ADHD letter goes with no leaflets: everything else a
 * family is signposted to comes from the service's "Options4CYP further help"
 * and "Links to signposting" documents, which are the clinician's signposting
 * pick-list written into the letter itself (adhdSignposting.js, TDI-230). A
 * leaflet with no path is named to the admin confirming the send and recorded
 * on the referral, but never offered to a family as a link.
 *
 * Mirrors python-functions/constants/rms_letter_leaflets.py, which the send
 * callable reads to record what went with the letter. A parity test pins the
 * two files to each other: keep one leaflet per line, in the same order.
 */

import { leafletsGoWithLetter } from './pathwaySettings';

export const LETTER_OUTCOME = {
  ACCEPTED: 'accepted',
  NOT_ACCEPTED: 'not_accepted',
};

// One leaflet per line, in the order they are shown. Keep in step with the
// backend mirror.
const LETTER_LEAFLETS = [
  { pathway: 'adhd', outcome: 'accepted', key: 'adhd_handy_apps', title: 'ADHD Handy Apps', path: '/rms/leaflets/adhd-apps.pdf' },
  { pathway: 'adhd', outcome: 'accepted', key: 'adhd_north_west', title: 'ADHD North West', path: '/rms/leaflets/adhd-north-west-what-we-do.png' },
];

// A leaflet the site hosts, at any environment's address or none: the same
// file on every environment (public/rms/leaflets/).
const HOSTED_LEAFLET = /^(?:https?:\/\/[^/]+)?(\/rms\/leaflets\/[A-Za-z0-9][A-Za-z0-9._-]*)$/i;

/**
 * The outcome key the leaflet list is keyed on, from the triage decision
 * summary's outcome ('accepted' | 'discharged') or a stored key.
 */
export function letterOutcomeKey(outcome) {
  const key = String(outcome || '').trim().toLowerCase();
  if (key === LETTER_OUTCOME.ACCEPTED) return LETTER_OUTCOME.ACCEPTED;
  if (key === 'discharged' || key === 'not_accepted' || key === 'rejected') return LETTER_OUTCOME.NOT_ACCEPTED;
  return null;
}

/**
 * The leaflets that go with this letter: none unless the pathway's settings
 * send leaflets and the decision is known.
 *
 * @param {string} [orgId]
 * @param {string} pathway - 'adhd' | 'asd'
 * @param {string} outcome - 'accepted' | 'discharged' | 'not_accepted'
 * @returns {Array<{key: string, title: string, url: string|null}>}
 */
export function leafletsForLetter(orgId, pathway, outcome) {
  if (!leafletsGoWithLetter(orgId, pathway)) return [];
  const outcomeKey = letterOutcomeKey(outcome);
  if (!outcomeKey) return [];
  const key = String(pathway || '').trim().toLowerCase();
  return LETTER_LEAFLETS
    .filter((l) => l.pathway === key && l.outcome === outcomeKey)
    .map(({ key: leafletKey, title, path }) => ({ key: leafletKey, title, url: path || null }));
}

/**
 * The leaflets recorded on a sent letter that a family or school can open:
 * only those with a link. Reads what the send stored on the referral, never
 * the current configuration, so a later change to the list does not rewrite
 * what went out.
 *
 * A leaflet the site hosts opens from the site the reader is on, by its path:
 * the send stores the sending environment's address, which on dev is not
 * https. Any other link must be https.
 *
 * @param {object} triageDocuments - the pathway's triageDocuments container
 * @returns {Array<{key: string, title: string, url: string}>}
 */
export function openableLetterLeaflets(triageDocuments) {
  const list = Array.isArray(triageDocuments?.leaflets) ? triageDocuments.leaflets : [];
  return list
    .filter((l) => l && typeof l.url === 'string' && l.title)
    .map(({ key, title, url }) => {
      const hosted = url.match(HOSTED_LEAFLET);
      if (hosted) return { key, title, url: hosted[1] };
      return /^https:\/\//i.test(url) ? { key, title, url } : null;
    })
    .filter(Boolean);
}
